import React, { useEffect, useRef, useState } from 'react';
import { Formik, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import InnerLayout from '../../components/layouts/innerLayout';
import InputField from '../../components/forms/InputField';
import { useDispatch, useSelector } from 'react-redux';
import { createProductAction, editProductAction, viewProductAction } from '../../redux/actions/productAction';
import { errorToast, successToast } from '../../components/toast';
import LoaderModal from '../../components/modals/loaderModal';
import { uploadImageAction } from '../../redux/actions/authAction';

const ProductForm = ({ }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [product, setProduct] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const imageInputRef = useRef(null);
    const accountData = useSelector((store) => store?.userReducer?.data);

    // Default values for product form fields
    const initialValues = {
        id: product?.id || "",
        name: product?.name || '',
        sku: product?.sku || '',
        price: product?.price || '',
        tax: product?.tax || null,
        discount: product?.discount || 0,
        description: product?.description || '',
        hsnCode: product?.hsnCode || '',
        barcode: product?.barcode || '',
        image: product?.image || '',
    };

    const validationSchema = Yup.object({
        name: Yup.string().required('Product name is required'),
        price: Yup.number().required('Product price is required').positive('Price must be a positive number'),
        discount: Yup.number().min(0, 'Discount rate must be 0 or greater'),
        description: Yup.string().optional(),
        hsnCode: Yup.string().optional(),
    });

    const handleSubmit = (values) => {
        setLoading(true)
        if (id) {
            console.log("Updated Product Data:", values);
            dispatch(editProductAction(values, (data) => { setLoading(false); successToast(data.data.message); navigate('/products') }, onError))
        } else {
            console.log("New Product Data:", values);
            dispatch(createProductAction(values, (data) => { setLoading(false); successToast(data.data.message); navigate('/products') }, onError))
        }
    };


    useEffect(() => {
        if (id) {
            fetchProductData();
        }
    }, [id]);

    const fetchProductData = async () => {
        setLoading(true)
        dispatch(viewProductAction({ id },
            (data) => {
                setProduct(data.data.data);
                setImagePreview(data.data.data.image)
                setLoading(false)
            },
            onError))
    };

    const handleImageChange = (event, setFieldValue) => {
        const imageUploadSuccess = (data) => {
            console.log(data.data, "success data");
            setLoading(false)
            setImagePreview(data.data.data);
            setFieldValue('image', data.data.data)
        }
        const file = event.target.files[0];
        setLoading(true)
        dispatch(uploadImageAction(file, imageUploadSuccess, onError));

    };

    const handleRemoveImage = (setFieldValue) => {
        setImagePreview(null);
        setFieldValue("image", null);
        // Clear the input field value manually using the ref
        if (imageInputRef.current) {
            imageInputRef.current.value = null;
        }
    };


    const onError = (data) => {
        setLoading(false)
        const errorMsg = data.data.message || "Unable to fetch try later";
        errorToast(errorMsg)
    }

    return (
        <InnerLayout module={id ? 'Edit Product' : 'Create Product'}>
            <div className="w-full h-full flex flex-col align-middle justify-center p-4">
                <div className="w-full max-w-5xl mx-auto pt-8 pb-6 px-6 bg-white rounded-md overflow-auto">
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        enableReinitialize
                    >
                        {({ values, setFieldValue }) => (
                            <Form className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <InputField
                                        label="Product Name"
                                        name="name"
                                        type="text"
                                        placeholder="Enter product name"
                                        value={values?.name}
                                    />
                                    <InputField
                                        label="SKU"
                                        name="sku"
                                        type="text"
                                        placeholder="Enter SKU"
                                        value={values?.sku}
                                    />
                                    <InputField
                                        label="Price"
                                        name="price"
                                        type="number"
                                        placeholder="Enter product price"
                                        value={values?.price}
                                    />
                                    <InputField
                                        label="Tax Rate (%)"
                                        name="tax"
                                        type="number"
                                        placeholder="Enter tax rate"
                                        value={values?.tax}
                                    />
                                    <InputField
                                        label="Discount Rate (%)"
                                        name="discount"
                                        type="number"
                                        placeholder="Enter discount rate"
                                        value={values?.discount}
                                    />
                                    <InputField
                                        label="Description"
                                        name="description"
                                        type="text"
                                        placeholder="Enter product description"
                                        value={values?.description}
                                    />
                                    <InputField
                                        label="Hsncode"
                                        name="hsnCode"
                                        type="text"
                                        placeholder="Enter product hsnCode"
                                        value={values?.hsnCode}
                                    />
                                    <InputField
                                        label="Barcode"
                                        name="barcode"
                                        type="text"
                                        placeholder="Enter product barcode"
                                        value={values?.barcode}
                                    />
                                    {accountData?.business_type === "restaurant" &&
                                        <div className="mb-4 text-start">
                                            <label htmlFor="image" className={`font-normal text-gray-700 text-sm mb-1`}>
                                                Product Image
                                            </label>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                id="image"
                                                className="w-full px-4 py-2 border rounded-md input-field"
                                                onChange={(e) => handleImageChange(e, setFieldValue)}
                                                ref={imageInputRef} // Attach ref to the input field
                                            />
                                            {imagePreview && (
                                                <div className="mt-4 flex gap-3 items-start mx-2">
                                                    <img
                                                        src={imagePreview}
                                                        alt="Product Preview"
                                                        className="w-40 h-40 object-cover rounded-md border"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveImage(setFieldValue)} // Call the remove function
                                                        className="mt-2 text-red-500 underline text-sm"
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            )}
                                            <ErrorMessage name="image" component="div" className="text-red-500 text-sm text-start" />
                                        </div>
                                    }
                                </div>

                                <div className="flex flex-wrap gap-3 justify-between mt-6">
                                    <button
                                        type="button"
                                        className="px-6 py-2 bg-gray-300 text-black rounded-md"
                                        onClick={() => navigate('/products')} // Navigate to the products list page (example)
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-6 py-2 bg-primary text-white rounded-md"
                                    >
                                        {id ? 'Update Product' : 'Create Product'}
                                    </button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
                {loading &&
                    <LoaderModal />
                }
            </div>
        </InnerLayout>
    );
};

export default ProductForm;
